import { useState } from 'react';
import { Label } from '@/components/ui/label';
import { LocationSelector } from '@/components/analytics/LocationSelector';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DateTimePicker } from '@/components/ui/datetime-picker';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  BarChart,
  MapPin,
  Smartphone,
  Calendar,
  Clock,
  Rss,
  RefreshCw,
  Filter,
  Globe,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { subDays } from 'date-fns';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { PresetSelector } from './preset-selector';
import type { FilterPreset, FilterValues } from '@/stores/analytics-preferences-store';

// Common timezone options
const TIMEZONE_OPTIONS = [
  { value: 'local', label: 'Local Time', offset: '' },
  { value: 'UTC', label: 'UTC', offset: '+00:00' },
  { value: 'America/New_York', label: 'Eastern Time (ET)', offset: '-05:00' },
  { value: 'America/Chicago', label: 'Central Time (CT)', offset: '-06:00' },
  { value: 'America/Denver', label: 'Mountain Time (MT)', offset: '-07:00' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)', offset: '-08:00' },
  { value: 'Europe/London', label: 'London (GMT)', offset: '+00:00' },
  { value: 'Europe/Paris', label: 'Paris (CET)', offset: '+01:00' },
  { value: 'Europe/Berlin', label: 'Berlin (CET)', offset: '+01:00' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)', offset: '+09:00' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)', offset: '+08:00' },
  { value: 'Asia/Kolkata', label: 'India (IST)', offset: '+05:30' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)', offset: '+04:00' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)', offset: '+10:00' },
];

export interface AnalyticsFiltersProps {
  // Location filter props
  locations: Array<{ id: string; name: string; subLocations?: any[] }>;
  selectedLocation: string;
  onLocationChange: (locationId: string) => void;

  // Device filter props
  devices: Array<{ id: string; name?: string | null; serial_number: string }>;
  selectedDevice: string;
  onDeviceChange: (deviceId: string) => void;

  // Date range filter props
  dateRange: { from: Date; to: Date };
  onDateRangeChange: (range: { from: Date; to: Date }) => void;

  // Interval filter props
  interval: string;
  onIntervalChange: (interval: string) => void;

  // Timezone props
  timezone: string;
  onTimezoneChange: (timezone: string) => void;

  // Live data toggle props
  liveData: boolean;
  onLiveDataToggle: (enabled: boolean) => void;

  // Refresh props
  isRefreshing: boolean;
  onRefresh: () => void;

  // Apply filters callback
  onApplyFilters: () => void;
  hasUnappliedChanges?: boolean;

  // Preset callbacks
  onLoadPreset: (preset: FilterPreset) => void;
  onResetToDefaults: () => void;
  /** Get current filter values for saving as preset */
  getCurrentFilters: () => FilterValues;

  // Optional className for styling
  className?: string;
}

/**
 * Enterprise-grade analytics filters component
 * Provides a responsive, modular filter system for analytics dashboard
 */
export function AnalyticsFilters({
  locations,
  selectedLocation,
  onLocationChange,
  devices,
  selectedDevice,
  onDeviceChange,
  dateRange,
  onDateRangeChange,
  interval,
  onIntervalChange,
  timezone,
  onTimezoneChange,
  liveData,
  onLiveDataToggle,
  isRefreshing,
  onRefresh,
  onApplyFilters,
  hasUnappliedChanges = false,
  onLoadPreset,
  onResetToDefaults,
  getCurrentFilters,
  className = '',
}: AnalyticsFiltersProps) {
  const [isFiltersOpen, setIsFiltersOpen] = useState(true);

  const handleDeviceChange = (value: string) => {
    onDeviceChange(value === 'all' ? '' : value);
  };

  const handleFromDateChange = (date: Date | undefined) => {
    if (date) {
      onDateRangeChange({ ...dateRange, from: date });
    }
  };

  const handleToDateChange = (date: Date | undefined) => {
    if (date) {
      onDateRangeChange({ ...dateRange, to: date });
    }
  };

  const handleQuickRange = (days: number) => {
    const now = new Date();
    onDateRangeChange({
      from: subDays(now, days),
      to: now,
    });
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Collapsible Filters Section */}
      <Collapsible open={isFiltersOpen} onOpenChange={setIsFiltersOpen}>
        <div className="flex items-center justify-between">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-2 p-0 h-auto hover:bg-transparent">
              <BarChart className="h-4 w-4" />
              <span className="text-sm font-medium text-muted-foreground">Filters & Controls</span>
              {hasUnappliedChanges && (
                <Badge variant="secondary" className="ml-2 text-xs bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                  Unsaved
                </Badge>
              )}
              {isFiltersOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </CollapsibleTrigger>

          {/* Mobile: Show Apply button in header when collapsed */}
          {!isFiltersOpen && hasUnappliedChanges && (
            <Button size="sm" onClick={onApplyFilters} disabled={isRefreshing} className="gap-2">
              <Filter className="h-4 w-4" />
              Apply
            </Button>
          )}
        </div>

        <CollapsibleContent className="mt-4">
          <div className="space-y-4">
            {/* Primary Filters Row */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {/* Location Filter */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <MapPin className="h-3 w-3" />
                  Location
                </Label>
                <LocationSelector locations={locations} onLocationChange={onLocationChange} value={selectedLocation} />
              </div>

              {/* Device Filter */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <Smartphone className="h-3 w-3" />
                  Device
                </Label>
                <Select value={selectedDevice || 'all'} onValueChange={handleDeviceChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select Device" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Devices</SelectItem>
                    {devices?.map((device) => (
                      <SelectItem key={device.id} value={device.id}>
                        {device.name || device.serial_number}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Interval Filter */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  Interval
                </Label>
                <Select value={interval} onValueChange={onIntervalChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Interval" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="realtime">Real Time (Raw)</SelectItem>
                    <SelectItem value="minute">Per Minute</SelectItem>
                    <SelectItem value="hour">Hourly</SelectItem>
                    <SelectItem value="day">Daily</SelectItem>
                    <SelectItem value="week">Weekly</SelectItem>
                    <SelectItem value="month">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Timezone Filter */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <Globe className="h-3 w-3" />
                  Timezone
                </Label>
                <Select value={timezone} onValueChange={onTimezoneChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select Timezone" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONE_OPTIONS.map((tz) => (
                      <SelectItem key={tz.value} value={tz.value}>
                        <span className="flex items-center gap-2">
                          <span>{tz.label}</span>
                          {tz.offset && <span className="text-xs text-muted-foreground">({tz.offset})</span>}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Date Range Section */}
            <div className="space-y-3">
              <Label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <Calendar className="h-3 w-3" />
                Date Range
              </Label>

              {/* Quick Range Buttons */}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickRange(1)}
                  className="text-xs h-8"
                >
                  Last 24h
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickRange(7)}
                  className="text-xs h-8"
                >
                  Last 7 days
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickRange(14)}
                  className="text-xs h-8"
                >
                  Last 14 days
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickRange(30)}
                  className="text-xs h-8"
                >
                  Last 30 days
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickRange(90)}
                  className="text-xs h-8"
                >
                  Last 90 days
                </Button>
              </div>

              {/* Custom Date Range Pickers */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">From</Label>
                  <DateTimePicker
                    value={dateRange.from}
                    onChange={handleFromDateChange}
                    onMonthChange={() => undefined}
                    hourCycle={12}
                    granularity="minute"
                    placeholder="Start date"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">To</Label>
                  <DateTimePicker
                    value={dateRange.to}
                    onChange={handleToDateChange}
                    onMonthChange={() => undefined}
                    hourCycle={12}
                    granularity="minute"
                    placeholder="End date"
                  />
                </div>
              </div>
            </div>

            <Separator className="opacity-50" />

            {/* Control Actions Row */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              {/* Left side: Preset Selector + Live Data Toggle */}
              <div className="flex items-center gap-3 flex-wrap">
                <PresetSelector
                  currentFilters={getCurrentFilters()}
                  onLoadPreset={onLoadPreset}
                  onResetToDefaults={onResetToDefaults}
                />

                <div className="flex items-center gap-2">
                  <Switch id="live-data" checked={liveData} onCheckedChange={onLiveDataToggle} />
                  <Label htmlFor="live-data" className="flex items-center gap-1.5 text-sm font-medium cursor-pointer">
                    <Rss className="h-4 w-4" />
                    <span className="hidden sm:inline">Live Data</span>
                    <span className="sm:hidden">Live</span>
                  </Label>
                </div>
              </div>

              {/* Right side: Action Buttons */}
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onRefresh}
                  disabled={isRefreshing}
                  className="gap-1.5"
                >
                  <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline">{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
                </Button>

                <Button
                  size="sm"
                  onClick={onApplyFilters}
                  disabled={isRefreshing}
                  className="gap-1.5"
                >
                  <Filter className="h-4 w-4" />
                  Apply Filters
                  {hasUnappliedChanges && (
                    <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 flex items-center justify-center rounded-full bg-white/20 text-[10px]">
                      !
                    </Badge>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
